import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, GraduationCap, Users, X, User, Mail, Phone, MapPin, Shield, CheckCircle2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthNavbar from "../../components/navigations/AuthNavbar";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import SearchableSelect from "../../components/agents/dashboard/SearchableSelect";
import { ghanaRegions, ghanaLocationsByRegion } from "../../data/ghanaRegionsAndLocations";
import clientService from "../../api/ClientService";
import { toast } from "react-toastify";

export default function NewRegistration() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  // Check account status and show blocking modal if under review
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck(true, () => {
    navigate("/dashboard");
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idValidationErrors, setIdValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    type: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDisplay: "", // For display purposes (0XXXXXXXXX or 233XXXXXXXXX)
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    agentCode: "",
    // ID Information (Optional) - for Others
    idType: "",
    issueDate: "",
    expiryDate: "",
    idFrontImage: null,
    idBackImage: null,
    card_number: "",
    // Student Information (Optional) - for Students
    schoolName: "",
    studentId: "",
    studentCardFront: null,
    studentCardBack: null,
    studentCardIssueDate: "",
    studentCardExpiryDate: "",
  });

  // Auto-populate agent code from Redux store
  useEffect(() => {
    if (user?.agent_code) {
      setFormData((prev) => ({ ...prev, agentCode: user.agent_code }));
    }
  }, [user]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData((prev) => ({ ...prev, type }));
  };

  const handleContinue = () => {
    if (selectedType) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    navigate("/dashboard");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field when user starts typing
    if (idValidationErrors[field]) {
      setIdValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // If clearing ID Type, clear all ID-related fields
    if (field === "idType" && !value) {
      setFormData((prev) => ({
        ...prev,
        card_number: "",
        issueDate: "",
        expiryDate: "",
        idFrontImage: null,
        idBackImage: null,
      }));
      setIdValidationErrors({});
    }
  };

  // Phone number validation and formatting
  const handlePhoneChange = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    
    let phoneDisplay = "";
    let phonePayload = "";
    
    if (digits.startsWith("233")) {
      // If starts with 233, allow up to 12 digits total
      phoneDisplay = digits.slice(0, 12);
      phonePayload = phoneDisplay;
    } else if (digits.startsWith("0")) {
      // If starts with 0, allow up to 10 digits
      phoneDisplay = digits.slice(0, 10);
      // Convert to 233 format for payload
      phonePayload = "233" + phoneDisplay.slice(1);
    } else {
      // If neither, allow up to 9 digits (will be prefixed with 233)
      phoneDisplay = digits.slice(0, 9);
      phonePayload = "233" + phoneDisplay;
    }
    
    setFormData((prev) => ({
      ...prev,
      phoneDisplay,
      phone: phonePayload,
    }));
  };

  // Validate phone number format
  const isPhoneValid = () => {
    if (!formData.phoneDisplay) return false;
    
    // Check if it starts with 233 (12 digits) or 0 (10 digits)
    if (formData.phoneDisplay.startsWith("233")) {
      return formData.phoneDisplay.length === 12;
    } else if (formData.phoneDisplay.startsWith("0")) {
      return formData.phoneDisplay.length === 10;
    }
    return false;
  };

  const handleImageUpload = (field, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (field) => {
    setFormData((prev) => ({ ...prev, [field]: null }));
    
    // Clear validation error when image is removed
    if (idValidationErrors[field]) {
      setIdValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Format DOB as DDMMYYYY
  const formatDOB = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  // Create payload for "Others" option (matching SignUp.jsx format without passwords)
  const createOthersPayload = () => {
    const documentTypeMapping = {
      "Ghana Card": "ghana_card",
      "Passport": "passport",
      "Driver License": "drivers_license",
      "Voter ID": "voter_id",
      "NHIS Card": "nhis_card",
    };

    // Base payload with required fields
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone, // Already formatted with 233
      location: formData.location === "Other"
        ? formData.customLocation
        : formData.location,
      dob: formatDOB(formData.dob),
      region: formData.region,
      gender: formData.gender.toLowerCase(),
      agent_code: formData.agentCode,
    };

    // Only include optional ID fields if they have values
    if (formData.idType) {
      payload.document_type = documentTypeMapping[formData.idType] || "";
      
      // Combine Ghana Card parts: GHA + middle 10 digits + check digit
      if (formData.ghanaCardMiddle && formData.ghanaCardCheck) {
        payload.card_number = `GHA${formData.ghanaCardMiddle}${formData.ghanaCardCheck}`;
      }
      
      if (formData.issueDate) {
        payload.issue_date = formData.issueDate;
      }
      
      if (formData.expiryDate) {
        payload.expiry_date = formData.expiryDate;
      }
      
      if (formData.idFrontImage) {
        payload.id_card_front = formData.idFrontImage;
      }
      
      if (formData.idBackImage) {
        payload.id_card_back = formData.idBackImage;
      }
    }

    return payload;
  };

  // Create payload for "Students" option — matches API spec
  const createStudentPayload = () => {
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone,
      location: formData.location === "Other"
        ? formData.customLocation
        : formData.location,
      dob: formatDOB(formData.dob),
      region: formData.region,
      gender: formData.gender.toLowerCase(),
    };
    if (formData.schoolName?.trim()) payload.school_name = formData.schoolName.trim();
    if (formData.studentId?.trim()) payload.student_school_id = formData.studentId.trim();
    if (formData.studentCardIssueDate) payload.card_issue_date = formData.studentCardIssueDate;
    if (formData.studentCardExpiryDate) payload.card_expiry_date = formData.studentCardExpiryDate;
    if (formData.studentCardFront) payload.id_card_front = formData.studentCardFront;
    if (formData.studentCardBack) payload.id_card_back = formData.studentCardBack;
    return payload;
  };

  // Check if any ID field is filled
  const hasAnyIdField = () => {
    return !!(
      formData.idType ||
      formData.ghanaCardMiddle ||
      formData.ghanaCardCheck ||
      formData.issueDate ||
      formData.expiryDate ||
      formData.idFrontImage ||
      formData.idBackImage
    );
  };

  // Check ID fields validation (without setting state - for use in isFormValid during render)
  const checkIdFieldsValidation = () => {
    if (!hasAnyIdField()) {
      return true; // No ID fields filled, validation passes
    }
    
    // If any ID field is filled, all must be filled
    if (!formData.idType) return false;
    if (!formData.ghanaCardMiddle?.trim() || formData.ghanaCardMiddle.length !== 9) return false;
    if (!formData.ghanaCardCheck?.trim() || formData.ghanaCardCheck.length !== 1) return false;
    if (!formData.issueDate) return false;
    if (!formData.expiryDate) return false;
    if (!formData.idFrontImage) return false;
    if (!formData.idBackImage) return false;
    
    return true;
  };

  // Validate ID fields - if any is filled, all must be filled (sets state for error messages)
  const validateIdFields = () => {
    const errors = {};
    
    if (hasAnyIdField()) {
      if (!formData.idType) {
        errors.idType = "ID Type is required when providing ID information";
      }
      if (!formData.ghanaCardMiddle?.trim()) {
        errors.ghanaCardMiddle = "Card number (middle 9 digits) is required";
      } else if (formData.ghanaCardMiddle.length !== 9) {
        errors.ghanaCardMiddle = "Card number must be exactly 9 digits";
      }
      if (!formData.ghanaCardCheck?.trim()) {
        errors.ghanaCardCheck = "Check digit is required";
      } else if (formData.ghanaCardCheck.length !== 1) {
        errors.ghanaCardCheck = "Check digit must be 1 digit";
      }
      if (!formData.issueDate) {
        errors.issueDate = "Issue Date is required when providing ID information";
      }
      if (!formData.expiryDate) {
        errors.expiryDate = "Expiry Date is required when providing ID information";
      }
      if (!formData.idFrontImage) {
        errors.idFrontImage = "ID Front Image is required when providing ID information";
      }
      if (!formData.idBackImage) {
        errors.idBackImage = "ID Back Image is required when providing ID information";
      }
    }
    
    setIdValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "gender",
      "dob",
      "region",
      "location",
      "agentCode",
    ];
    
    // Check all required fields are filled
    const allFieldsFilled = requiredFields.every((field) => {
      if (field === "location") {
        return formData.location || (formData.location === "Other" && formData.customLocation);
      }
      return formData[field]?.trim();
    });
    
    // Check phone number is valid
    const phoneValid = isPhoneValid();
    
    // Check ID fields validation (if any ID field is filled, all must be filled)
    // Use checkIdFieldsValidation instead of validateIdFields to avoid state updates during render
    const idFieldsValid = checkIdFieldsValidation();
    
    return allFieldsFilled && phoneValid && idFieldsValid;
  };

  // Handle form submission for "Others" registration
  const handleSubmitRegistration = async () => {
    // Validate ID fields first
    if (hasAnyIdField()) {
      const idFieldsValid = validateIdFields();
      if (!idFieldsValid) {
        toast.error("Please complete all ID information fields or leave them all empty");
        return;
      }
    }

    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createOthersPayload();
      const response = await clientService.createClient(payload);

      if (response.status) {
        toast.success(response.message || "Client registered successfully!");
        navigate("/others-clients");
      } else {
        toast.error(response.message || "Failed to register client");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submission for "Students" registration
  const handleSubmitStudentRegistration = async () => {
    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createStudentPayload();
      const response = await clientService.createStudent(payload);

      if (response.status) {
        toast.success(response.message || "Student registered successfully!");
        navigate("/students-clients");
      } else {
        toast.error(response.message || "Failed to register student");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Student registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
      {/* Account Status Modal - Blocks all interactions when account is under review */}
      <AccountStatusModal />
      
      {/* Blur and disable content when blocked */}
      <div className={isBlocked ? "pointer-events-none opacity-30 blur-sm transition-all duration-300" : "transition-all duration-300"}>
        <AuthNavbar />

      {/* Page Header */}
      <div className="relative bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 shadow-lg">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleClose}
                className="p-2 text-white/80 transition-all duration-200 hover:text-white hover:bg-white/20 rounded-lg backdrop-blur-sm"
              >
                <X className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-7 h-7" />
                  New Registration
                </h1>
                <p className="mt-1 text-sm text-emerald-100">
                  Register new clients for travel insurance
                </p>
              </div>
            </div>

            {/* Progress Steps */}
            {(selectedType === "others" || selectedType === "students") && (
              <div className="hidden items-center space-x-3 md:flex bg-white/20 backdrop-blur-md rounded-full px-4 py-2">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                        currentStep >= step
                          ? "bg-white text-emerald-600 shadow-lg scale-110"
                          : "bg-white/30 text-white/70"
                      }`}
                    >
                      {currentStep > step ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        step
                      )}
                    </div>
                    {step < 4 && (
                      <div
                        className={`w-12 h-1 mx-2 rounded-full transition-all duration-300 ${
                          currentStep > step ? "bg-white" : "bg-white/30"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-10"
            >
              {/* Header */}
              <div className="text-center space-y-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl mb-4"
                >
                  <Shield className="w-10 h-10 text-white" />
                </motion.div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent md:text-5xl">
                  Start New Registration
                </h1>
                <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
                  Choose the type of registration you want to start. Select from the options below to proceed.
                </p>
              </div>

              {/* Type Selection Cards */}
              <div className="grid grid-cols-1 gap-8 mx-auto max-w-5xl lg:grid-cols-2">
                {/* Students Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleTypeSelect("students")}
                  className={`relative cursor-pointer rounded-2xl border-2 transition-all duration-300 overflow-hidden group ${
                    selectedType === "students"
                      ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-2xl ring-4 ring-emerald-200"
                      : "border-gray-200 bg-white hover:border-emerald-300 hover:shadow-xl"
                  }`}
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-teal-500/0 transition-all duration-300 ${
                    selectedType === "students" ? "opacity-100" : "group-hover:opacity-5"
                  }`}></div>
                  
                  {selectedType === "students" && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      className="flex absolute -top-4 -right-4 justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg z-10"
                    >
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </motion.div>
                  )}

                  <div className="relative p-10 text-center">
                    {/* Icon with gradient background */}
                    <motion.div 
                      className={`flex justify-center items-center mx-auto mb-6 w-20 h-20 rounded-2xl transition-all duration-300 ${
                        selectedType === "students"
                          ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-xl"
                          : "bg-gradient-to-br from-emerald-100 to-teal-100 group-hover:from-emerald-200 group-hover:to-teal-200"
                      }`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <GraduationCap className={`w-10 h-10 transition-colors ${
                        selectedType === "students" ? "text-white" : "text-emerald-600"
                      }`} />
                    </motion.div>
                    
                    <h3 className={`mb-3 text-2xl font-bold transition-colors ${
                      selectedType === "students" ? "text-emerald-700" : "text-gray-900"
                    }`}>
                      Students
                    </h3>
                    <p className="text-base leading-relaxed text-gray-600">
                      Register students for travel insurance coverage with
                      special rates and benefits tailored for educational travel
                    </p>
                    
                    {/* Feature badges */}
                    <div className="mt-6 flex justify-center gap-2 flex-wrap">
                      <span className="px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                        Special Rates
                      </span>
                      <span className="px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                        Student Benefits
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Others Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleTypeSelect("others")}
                  className={`relative cursor-pointer rounded-2xl border-2 transition-all duration-300 overflow-hidden group ${
                    selectedType === "others"
                      ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-2xl ring-4 ring-emerald-200"
                      : "border-gray-200 bg-white hover:border-emerald-300 hover:shadow-xl"
                  }`}
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 transition-all duration-300 ${
                    selectedType === "others" ? "opacity-100" : "group-hover:opacity-5"
                  }`}></div>
                  
                  {selectedType === "others" && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      className="flex absolute -top-4 -right-4 justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg z-10"
                    >
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </motion.div>
                  )}

                  <div className="relative p-10 text-center">
                    {/* Icon with gradient background */}
                    <motion.div 
                      className={`flex justify-center items-center mx-auto mb-6 w-20 h-20 rounded-2xl transition-all duration-300 ${
                        selectedType === "others"
                          ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-xl"
                          : "bg-gradient-to-br from-blue-100 to-cyan-100 group-hover:from-blue-200 group-hover:to-cyan-200"
                      }`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <Users className={`w-10 h-10 transition-colors ${
                        selectedType === "others" ? "text-white" : "text-blue-600"
                      }`} />
                    </motion.div>
                    
                    <h3 className={`mb-3 text-2xl font-bold transition-colors ${
                      selectedType === "others" ? "text-emerald-700" : "text-gray-900"
                    }`}>
                      Others
                    </h3>
                    <p className="text-base leading-relaxed text-gray-600">
                      Register individuals for general travel insurance coverage
                      and comprehensive protection for all types of travelers
                    </p>
                    
                    {/* Feature badges */}
                    <div className="mt-6 flex justify-center gap-2 flex-wrap">
                      <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        Full Coverage
                      </span>
                      <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        All Travelers
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Continue Button */}
              <div className="flex justify-center pt-8">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleContinue}
                  disabled={!selectedType}
                  className={`group relative px-12 py-5 text-lg rounded-2xl font-semibold text-white transition-all duration-300 overflow-hidden ${
                    selectedType
                      ? "bg-gradient-to-r from-emerald-600 to-teal-600 shadow-xl hover:shadow-2xl hover:from-emerald-700 hover:to-teal-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  {selectedType && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    />
                  )}
                  <span className="relative flex items-center">
                    Continue
                    <ArrowRight className="inline-block ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 2 && selectedType === "others" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="space-y-4">
                <h1 className="flex items-center gap-4 text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent md:text-5xl">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg"
                  >
                    <User className="w-6 h-6 text-white" />
                  </motion.div>
                  Personal Information
                </h1>
                <p className="mt-3 text-lg text-gray-600 max-w-2xl">
                  Please provide your basic information to continue with the registration process
                </p>
              </div>

              {/* Form Fields */}
              <div className="mx-auto space-y-8 max-w-7xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500"></div>
                  
                  <div className="space-y-8">
                    {/* Name Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Personal Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) =>
                              handleInputChange("firstName", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your first name"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) =>
                              handleInputChange("lastName", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your last name"
                          />
                        </div>
                      </div>
                      
                      {/* Gender and Date of Birth */}
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-6">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Gender <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-3">
                            {["Male", "Female"].map((option) => (
                              <motion.label
                                key={option}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`flex items-center gap-3 px-4 py-3 flex-1 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                                  formData.gender === option
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                                onClick={() => handleInputChange("gender", option)}
                              >
                                <div
                                  className={`relative w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                                    formData.gender === option
                                      ? "border-emerald-500 bg-emerald-500"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {formData.gender === option && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="absolute inset-0.5 rounded-full bg-white"
                                    />
                                  )}
                                </div>
                                <span
                                  className={`text-sm font-medium transition-colors ${
                                    formData.gender === option
                                      ? "text-emerald-700"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {option}
                                </span>
                              </motion.label>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Date of Birth <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.dob}
                            onChange={(e) =>
                              handleInputChange("dob", e.target.value)
                            }
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                          />
                          {formData.dob && (
                            <p className="text-xs text-gray-500 mt-1">
                              {(() => {
                                const dob = new Date(formData.dob);
                                const today = new Date();
                                const age = today.getFullYear() - dob.getFullYear();
                                const monthDiff = today.getMonth() - dob.getMonth();
                                const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) ? age - 1 : age;
                                return `Age: ${actualAge} years`;
                              })()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Contact Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <Mail className="w-4 h-4 text-emerald-600" />
                            Guardian Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter guardian email address"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <Phone className="w-4 h-4 text-emerald-600" />
                            Guardian Phone <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="flex items-center">
                              <div className="px-4 py-4 text-base font-medium text-gray-600 border-r-2 border-gray-200 bg-gray-50 rounded-l-xl">
                                233
                              </div>
                              <input
                                type="tel"
                                value={formData.phoneDisplay}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className="px-5 py-4 flex-1 text-base rounded-r-xl border-2 border-l-0 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                                placeholder={formData.phoneDisplay.startsWith("0") ? "0XXXXXXXXX" : "XXXXXXXXX"}
                                maxLength={formData.phoneDisplay.startsWith("0") ? 10 : formData.phoneDisplay.startsWith("233") ? 12 : 9}
                              />
                            </div>
                          </div>
                          {formData.phoneDisplay && (
                            <p className={`text-xs mt-1 ${isPhoneValid() ? "text-green-600" : "text-red-600"}`}>
                              {isPhoneValid() 
                                ? `✓ Valid format (${formData.phoneDisplay.startsWith("0") ? "10 digits" : "12 digits"})`
                                : `Invalid format. Use ${formData.phoneDisplay.startsWith("0") ? "0XXXXXXXXX (10 digits)" : "233XXXXXXXXX (12 digits)"}`
                              }
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Enter starting with 233 (12 digits) or 0 (10 digits)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Location Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <MapPin className="w-5 h-5 text-purple-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Location Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            Region <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            options={ghanaRegions}
                            value={formData.region}
                            onChange={(value) => {
                              handleInputChange("region", value);
                              handleInputChange("location", "");
                              handleInputChange("customLocation", "");
                            }}
                            placeholder="Search and select a region"
                            disabled={false}
                            showOther={false}
                            label="region"
                            required={true}
                            dropup={true}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            Location <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            options={formData.region ? (ghanaLocationsByRegion[formData.region] || []) : []}
                            value={formData.location}
                            onChange={(value) => {
                              if (value === "Other") {
                                handleInputChange("location", "Other");
                              } else {
                                handleInputChange("location", value);
                                handleInputChange("customLocation", "");
                              }
                            }}
                            placeholder={formData.region ? "Search and select a location" : "Select a region first"}
                            disabled={!formData.region}
                            showOther={true}
                            otherPlaceholder="Enter custom location"
                            label="location"
                            required={true}
                            dropup={true}
                          />
                          {formData.location === "Other" && (
                            <input
                              type="text"
                              value={formData.customLocation}
                              onChange={(e) => handleInputChange("customLocation", e.target.value)}
                              className="mt-2 px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                              placeholder="Enter your custom location"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Agent Code Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Shield className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Agent Information</h3>
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                          <Shield className="w-4 h-4 text-emerald-600" />
                          Agent Code <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.agentCode}
                            readOnly
                            disabled
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 cursor-not-allowed transition-all duration-200 font-medium text-gray-700"
                            placeholder="Agent code will be auto-filled"
                          />
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Your agent code is automatically filled from your account</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep(3)}
                  disabled={!isFormValid()}
                  className={`px-10 py-4 text-lg font-semibold text-white rounded-xl transition-all duration-200 ${
                    isFormValid()
                      ? "bg-emerald-600 shadow-lg hover:bg-emerald-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Continue
                  <ArrowRight className="inline-block ml-3 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 2 && selectedType === "students" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="space-y-4">
                <h1 className="flex items-center gap-4 text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent md:text-5xl">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg"
                  >
                    <GraduationCap className="w-6 h-6 text-white" />
                  </motion.div>
                  Personal Information
                </h1>
                <p className="mt-3 text-lg text-gray-600 max-w-2xl">
                  Please provide your basic information to continue with the student registration process
                </p>
              </div>

              {/* Form Fields */}
              <div className="mx-auto space-y-8 max-w-7xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500"></div>
                  
                  <div className="space-y-8">
                    {/* Name Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Personal Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <User className="w-4 h-4 text-emerald-600" />
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) =>
                              handleInputChange("firstName", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your first name"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <User className="w-4 h-4 text-emerald-600" />
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) =>
                              handleInputChange("lastName", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your last name"
                          />
                        </div>
                      </div>
                      
                      {/* Gender and Date of Birth */}
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-6">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Gender <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-3">
                            {["Male", "Female"].map((option) => (
                              <motion.label
                                key={option}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`flex items-center gap-3 px-4 py-3 flex-1 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                                  formData.gender === option
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                                onClick={() => handleInputChange("gender", option)}
                              >
                                <div
                                  className={`relative w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                                    formData.gender === option
                                      ? "border-emerald-500 bg-emerald-500"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {formData.gender === option && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="absolute inset-0.5 rounded-full bg-white"
                                    />
                                  )}
                                </div>
                                <span
                                  className={`text-sm font-medium transition-colors ${
                                    formData.gender === option
                                      ? "text-emerald-700"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {option}
                                </span>
                              </motion.label>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Date of Birth <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.dob}
                            onChange={(e) =>
                              handleInputChange("dob", e.target.value)
                            }
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                          />
                          {formData.dob && (
                            <p className="text-xs text-gray-500 mt-1">
                              {(() => {
                                const dob = new Date(formData.dob);
                                const today = new Date();
                                const age = today.getFullYear() - dob.getFullYear();
                                const monthDiff = today.getMonth() - dob.getMonth();
                                const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) ? age - 1 : age;
                                return `Age: ${actualAge} years`;
                              })()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Contact Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <Mail className="w-4 h-4 text-emerald-600" />
                            Guardian Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter guardian email address"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <Phone className="w-4 h-4 text-emerald-600" />
                            Guardian Phone <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="flex items-center">
                              <div className="px-4 py-4 text-base font-medium text-gray-600 border-r-2 border-gray-200 bg-gray-50 rounded-l-xl">
                                233
                              </div>
                              <input
                                type="tel"
                                value={formData.phoneDisplay}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className="px-5 py-4 flex-1 text-base rounded-r-xl border-2 border-l-0 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                                placeholder={formData.phoneDisplay.startsWith("0") ? "0XXXXXXXXX" : "XXXXXXXXX"}
                                maxLength={formData.phoneDisplay.startsWith("0") ? 10 : formData.phoneDisplay.startsWith("233") ? 12 : 9}
                              />
                            </div>
                          </div>
                          {formData.phoneDisplay && (
                            <p className={`text-xs mt-1 ${isPhoneValid() ? "text-green-600" : "text-red-600"}`}>
                              {isPhoneValid() 
                                ? `✓ Valid format (${formData.phoneDisplay.startsWith("0") ? "10 digits" : "12 digits"})`
                                : `Invalid format. Use ${formData.phoneDisplay.startsWith("0") ? "0XXXXXXXXX (10 digits)" : "233XXXXXXXXX (12 digits)"}`
                              }
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Enter starting with 233 (12 digits) or 0 (10 digits)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Location Fields Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <MapPin className="w-5 h-5 text-purple-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Location Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            Region <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            options={ghanaRegions}
                            value={formData.region}
                            onChange={(value) => {
                              handleInputChange("region", value);
                              handleInputChange("location", "");
                              handleInputChange("customLocation", "");
                            }}
                            placeholder="Search and select a region"
                            disabled={false}
                            showOther={false}
                            label="region"
                            required={true}
                            dropup={true}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            Location <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            options={formData.region ? (ghanaLocationsByRegion[formData.region] || []) : []}
                            value={formData.location}
                            onChange={(value) => {
                              if (value === "Other") {
                                handleInputChange("location", "Other");
                              } else {
                                handleInputChange("location", value);
                                handleInputChange("customLocation", "");
                              }
                            }}
                            placeholder={formData.region ? "Search and select a location" : "Select a region first"}
                            disabled={!formData.region}
                            showOther={true}
                            otherPlaceholder="Enter custom location"
                            label="location"
                            required={true}
                            dropup={true}
                          />
                          {formData.location === "Other" && (
                            <input
                              type="text"
                              value={formData.customLocation}
                              onChange={(e) => handleInputChange("customLocation", e.target.value)}
                              className="mt-2 px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                              placeholder="Enter your custom location"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Agent Code Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Shield className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Agent Information</h3>
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                          <Shield className="w-4 h-4 text-emerald-600" />
                          Agent Code <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.agentCode}
                            readOnly
                            disabled
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 cursor-not-allowed transition-all duration-200 font-medium text-gray-700"
                            placeholder="Agent code will be auto-filled"
                          />
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Your agent code is automatically filled from your account</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05, x: -3 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="group px-8 py-4 text-lg font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 hover:border-gray-300 hover:shadow-md"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: isFormValid() ? 1.05 : 1, y: isFormValid() ? -2 : 0 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep(3)}
                  disabled={!isFormValid()}
                  className={`group relative px-10 py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 overflow-hidden ${
                    isFormValid()
                      ? "bg-gradient-to-r from-emerald-600 to-teal-600 shadow-xl hover:shadow-2xl"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  {isFormValid() && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    />
                  )}
                  <span className="relative flex items-center">
                    Continue
                    <ArrowRight className="inline-block ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && selectedType === "students" && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
                  Student Information (Optional)
                </h1>
                <p className="mt-3 text-lg text-gray-600">
                  Provide your student details for enhanced verification
                </p>
              </div>

              {/* Form Fields */}
              <div className="mx-auto space-y-8 max-w-7xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>
                  
                  <div className="space-y-8">
                    {/* School Information Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <GraduationCap className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">School Information</h3>
                        <span className="ml-auto px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">Optional</span>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            School Name
                          </label>
                          <input
                            type="text"
                            value={formData.schoolName}
                            onChange={(e) =>
                              handleInputChange("schoolName", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your school name"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Student ID
                          </label>
                          <input
                            type="text"
                            value={formData.studentId}
                            onChange={(e) =>
                              handleInputChange("studentId", e.target.value)
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                            placeholder="Enter your student ID"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Student Card Dates Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Shield className="w-5 h-5 text-indigo-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Student Card Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Issue Date
                          </label>
                          <input
                            type="date"
                            value={formData.studentCardIssueDate}
                            onChange={(e) =>
                              handleInputChange(
                                "studentCardIssueDate",
                                e.target.value
                              )
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Expiry Date
                          </label>
                          <input
                            type="date"
                            value={formData.studentCardExpiryDate}
                            onChange={(e) =>
                              handleInputChange(
                                "studentCardExpiryDate",
                                e.target.value
                              )
                            }
                            className="px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Student Card Image Uploads */}
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
                  
                  <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Shield className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Student Card Images</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    {/* Front Card */}
                    <div>
                      <label className="flex items-center gap-2 mb-4 text-base font-semibold text-gray-700">
                        Student Card Front
                      </label>
                      <div className="space-y-4">
                        {formData.studentCardFront ? (
                          <div className="relative group">
                            <img
                              src={formData.studentCardFront}
                              alt="Student Card Front"
                              className="object-cover w-full h-48 rounded-xl border-2 border-gray-200 shadow-md transition-transform group-hover:scale-105"
                            />
                            <button
                              onClick={() => removeImage("studentCardFront")}
                              className="flex absolute -top-3 -right-3 justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-all hover:bg-red-600 hover:scale-110 shadow-lg"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label className="block w-full h-48 rounded-xl border-2 border-gray-300 border-dashed transition-all cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 group">
                            <div className="flex flex-col justify-center items-center h-full text-gray-600 group-hover:text-emerald-600 transition-colors">
                              <svg
                                className="mb-3 w-12 h-12"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-base font-medium">
                                Upload Front Image
                              </span>
                              <span className="text-xs text-gray-500 mt-1">Click to browse</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload(
                                  "studentCardFront",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Back Card */}
                    <div>
                      <label className="flex items-center gap-2 mb-4 text-base font-semibold text-gray-700">
                        Student Card Back
                      </label>
                      <div className="space-y-4">
                        {formData.studentCardBack ? (
                          <div className="relative group">
                            <img
                              src={formData.studentCardBack}
                              alt="Student Card Back"
                              className="object-cover w-full h-48 rounded-xl border-2 border-gray-200 shadow-md transition-transform group-hover:scale-105"
                            />
                            <button
                              onClick={() => removeImage("studentCardBack")}
                              className="flex absolute -top-3 -right-3 justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-all hover:bg-red-600 hover:scale-110 shadow-lg"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label className="block w-full h-48 rounded-xl border-2 border-gray-300 border-dashed transition-all cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 group">
                            <div className="flex flex-col justify-center items-center h-full text-gray-600 group-hover:text-emerald-600 transition-colors">
                              <svg
                                className="mb-3 w-12 h-12"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-base font-medium">Upload Back Image</span>
                              <span className="text-xs text-gray-500 mt-1">Click to browse</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload(
                                  "studentCardBack",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep(4)}
                  className="px-10 py-4 text-lg font-semibold text-white bg-emerald-600 rounded-xl shadow-lg transition-colors hover:bg-emerald-700"
                >
                  Continue
                  <ArrowRight className="inline-block ml-3 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && selectedType === "others" && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
                  ID Information (Optional)
                </h1>
                <p className="mt-3 text-lg text-gray-600">
                  Provide your identification details for enhanced verification
                </p>
                {hasAnyIdField() && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Since you've started providing ID information, all ID fields below are now required.
                    </p>
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div className="mx-auto space-y-8 max-w-7xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500"></div>
                  
                  <div className="space-y-8">
                    {/* ID Type and Dates Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Shield className="w-5 h-5 text-orange-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">ID Information</h3>
                        <span className="ml-auto px-3 py-1 text-xs font-medium bg-orange-50 text-orange-700 rounded-full">Optional</span>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            ID Type {hasAnyIdField() && <span className="text-red-500">*</span>}
                          </label>
                          <select
                            value={formData.idType}
                            onChange={(e) => {
                              handleInputChange("idType", e.target.value);
                            }}
                            className={`px-5 py-4 w-full text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 ${
                              idValidationErrors.idType
                                ? "border-red-500 bg-red-50"
                                : "border-gray-200 bg-gray-50"
                            }`}
                          >
                            <option value="">Select ID type</option>
                            <option value="Ghana Card">Ghana Card</option>
                          </select>
                          {idValidationErrors.idType && (
                            <p className="text-sm text-red-600 mt-1">{idValidationErrors.idType}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Card Number {hasAnyIdField() && <span className="text-red-500">*</span>}
                          </label>
                          <div className="flex items-center gap-2 w-full">
                            {/* GHA prefix (readonly) */}
                            <div className="flex-shrink-0">
                              <input
                                type="text"
                                value="GHA"
                                readOnly
                                disabled={!formData.idType}
                                className="px-4 py-4 w-20 text-base rounded-xl border-2 border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed font-semibold uppercase text-center"
                              />
                            </div>
                            {/* First hyphen separator */}
                            <div className="flex-shrink-0 flex items-center justify-center w-6">
                              <span className="text-2xl font-semibold text-gray-500">-</span>
                            </div>
                            {/* Middle 10 digits */}
                            <div className="flex-1">
                              <input
                                type="text"
                                value={formData.ghanaCardMiddle}
                                onChange={(e) => {
                                  // Only allow digits, max 10
                                  const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
                                  handleInputChange("ghanaCardMiddle", digits);
                                }}
                                disabled={!formData.idType}
                                placeholder="123456789"
                                maxLength={9}
                                className={`px-5 py-4 w-full text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  idValidationErrors.ghanaCardMiddle
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-200 bg-gray-50"
                                }`}
                              />
                            </div>
                            {/* Second hyphen separator */}
                            <div className="flex-shrink-0 flex items-center justify-center w-6">
                              <span className="text-2xl font-semibold text-gray-500">-</span>
                            </div>
                            {/* Check digit */}
                            <div className="flex-shrink-0">
                              <input
                                type="text"
                                value={formData.ghanaCardCheck}
                                onChange={(e) => {
                                  // Only allow digits, max 1
                                  const digit = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
                                  handleInputChange("ghanaCardCheck", digit);
                                }}
                                disabled={!formData.idType}
                                placeholder="X"
                                maxLength={1}
                                className={`px-4 py-4 w-16 text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed text-center ${
                                  idValidationErrors.ghanaCardCheck
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-200 bg-gray-50"
                                }`}
                              />
                            </div>
                          </div>
                          {idValidationErrors.ghanaCardMiddle && (
                            <p className="text-sm text-red-600 mt-1">{idValidationErrors.ghanaCardMiddle}</p>
                          )}
                          {idValidationErrors.ghanaCardCheck && (
                            <p className="text-sm text-red-600 mt-1">{idValidationErrors.ghanaCardCheck}</p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-6">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Issue Date {hasAnyIdField() && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="date"
                            value={formData.issueDate}
                            onChange={(e) =>
                              handleInputChange("issueDate", e.target.value)
                            }
                            className={`px-5 py-4 w-full text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 ${
                              idValidationErrors.issueDate
                                ? "border-red-500 bg-red-50"
                                : "border-gray-200 bg-gray-50"
                            }`}
                          />
                          {idValidationErrors.issueDate && (
                            <p className="text-sm text-red-600 mt-1">{idValidationErrors.issueDate}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                            Expiry Date {hasAnyIdField() && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="date"
                            value={formData.expiryDate}
                            onChange={(e) =>
                              handleInputChange("expiryDate", e.target.value)
                            }
                            className={`px-5 py-4 w-full text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 ${
                              idValidationErrors.expiryDate
                                ? "border-red-500 bg-red-50"
                                : "border-gray-200 bg-gray-50"
                            }`}
                          />
                          {idValidationErrors.expiryDate && (
                            <p className="text-sm text-red-600 mt-1">{idValidationErrors.expiryDate}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ID Image Uploads */}
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden relative">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-pink-500 to-red-500"></div>
                  
                  <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Shield className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">ID Images</h3>
                    {hasAnyIdField() && <span className="ml-auto px-3 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-full">Required</span>}
                  </div>
                  
                  {hasAnyIdField() && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> Since you've started providing ID information, all ID fields are now required.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    {/* Front ID */}
                    <div>
                      <label className="flex items-center gap-2 mb-4 text-base font-semibold text-gray-700">
                        ID Front Image {hasAnyIdField() && <span className="text-red-500">*</span>}
                      </label>
                      <div className="space-y-4">
                        {formData.idFrontImage ? (
                          <div className="relative group">
                            <img
                              src={formData.idFrontImage}
                              alt="ID Front"
                              className="object-cover w-full h-48 rounded-xl border-2 border-gray-200 shadow-md transition-transform group-hover:scale-105"
                            />
                            <button
                              onClick={() => removeImage("idFrontImage")}
                              className="flex absolute -top-3 -right-3 justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-all hover:bg-red-600 hover:scale-110 shadow-lg"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label className={`block w-full h-48 rounded-xl border-2 border-dashed transition-all cursor-pointer group ${
                            idValidationErrors.idFrontImage
                              ? "border-red-500 bg-red-50/50 hover:border-red-600"
                              : "border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50"
                          }`}>
                            <div className={`flex flex-col justify-center items-center h-full transition-colors ${
                              idValidationErrors.idFrontImage
                                ? "text-red-600"
                                : "text-gray-600 group-hover:text-emerald-600"
                            }`}>
                              <svg
                                className="mb-3 w-12 h-12"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-base font-medium">
                                Upload Front Image
                              </span>
                              <span className="text-xs text-gray-500 mt-1">Click to browse</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload(
                                  "idFrontImage",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                        {idValidationErrors.idFrontImage && (
                          <p className="text-sm text-red-600">{idValidationErrors.idFrontImage}</p>
                        )}
                      </div>
                    </div>

                    {/* Back ID */}
                    <div>
                      <label className="flex items-center gap-2 mb-4 text-base font-semibold text-gray-700">
                        ID Back Image {hasAnyIdField() && <span className="text-red-500">*</span>}
                      </label>
                      <div className="space-y-4">
                        {formData.idBackImage ? (
                          <div className="relative group">
                            <img
                              src={formData.idBackImage}
                              alt="ID Back"
                              className="object-cover w-full h-48 rounded-xl border-2 border-gray-200 shadow-md transition-transform group-hover:scale-105"
                            />
                            <button
                              onClick={() => removeImage("idBackImage")}
                              className="flex absolute -top-3 -right-3 justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-all hover:bg-red-600 hover:scale-110 shadow-lg"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label className={`block w-full h-48 rounded-xl border-2 border-dashed transition-all cursor-pointer group ${
                            idValidationErrors.idBackImage
                              ? "border-red-500 bg-red-50/50 hover:border-red-600"
                              : "border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50"
                          }`}>
                            <div className={`flex flex-col justify-center items-center h-full transition-colors ${
                              idValidationErrors.idBackImage
                                ? "text-red-600"
                                : "text-gray-600 group-hover:text-emerald-600"
                            }`}>
                              <svg
                                className="mb-3 w-12 h-12"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-base font-medium">Upload Back Image</span>
                              <span className="text-xs text-gray-500 mt-1">Click to browse</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload(
                                  "idBackImage",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                        {idValidationErrors.idBackImage && (
                          <p className="text-sm text-red-600">{idValidationErrors.idBackImage}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // Validate ID fields before proceeding
                    if (hasAnyIdField()) {
                      const isValid = validateIdFields();
                      if (!isValid) {
                        toast.error("Please complete all ID information fields or leave them all empty");
                        return;
                      }
                    }
                    setCurrentStep(4);
                  }}
                  className="px-10 py-4 text-lg font-semibold text-white bg-emerald-600 rounded-xl shadow-lg transition-colors hover:bg-emerald-700"
                >
                  Continue
                  <ArrowRight className="inline-block ml-3 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 4 && selectedType === "students" && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
                  Review & Submit
                </h1>
                <p className="mt-3 text-lg text-gray-600">
                  Please review your information before submitting
                </p>
              </div>

              {/* Preview Card */}
              <div className="mx-auto max-w-5xl">
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-xl">
                  {/* Header */}
                  <div className="px-8 py-6 bg-gradient-to-r from-emerald-500 to-emerald-600">
                    <h2 className="text-2xl font-semibold text-white">
                      Registration Summary
                    </h2>
                    <p className="text-lg text-emerald-100">
                      Please verify all information is correct
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-8 space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                        <div className="mr-4 w-3 h-3 bg-emerald-500 rounded-full"></div>
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            First Name
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.firstName}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Last Name
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.lastName}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Guardian Email
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.email}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Guardian Phone
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.phone}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Region
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.region}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Location
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.location}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Agent Code
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.agentCode}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Student Information (if provided) */}
                    {(formData.schoolName ||
                      formData.studentId ||
                      formData.studentCardIssueDate ||
                      formData.studentCardExpiryDate ||
                      formData.studentCardFront ||
                      formData.studentCardBack) && (
                      <div>
                        <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                          <div className="mr-4 w-3 h-3 bg-blue-500 rounded-full"></div>
                          Student Information
                        </h3>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          {formData.schoolName && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                School Name
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {formData.schoolName}
                              </p>
                            </div>
                          )}
                          {formData.studentId && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                Student ID
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {formData.studentId}
                              </p>
                            </div>
                          )}
                          {formData.studentCardIssueDate && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                Issue Date
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {new Date(
                                  formData.studentCardIssueDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {formData.studentCardExpiryDate && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                Expiry Date
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {new Date(
                                  formData.studentCardExpiryDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Student Card Images */}
                        {(formData.studentCardFront ||
                          formData.studentCardBack) && (
                          <div className="mt-6">
                            <label className="block mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
                              Student Card Images
                            </label>
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                              {formData.studentCardFront && (
                                <div className="p-6 bg-gray-50 rounded-xl">
                                  <label className="block mb-3 text-sm font-medium tracking-wide text-gray-500 uppercase">
                                    Front
                                  </label>
                                  <img
                                    src={formData.studentCardFront}
                                    alt="Student Card Front"
                                    className="object-cover w-full h-32 rounded-lg border"
                                  />
                                </div>
                              )}
                              {formData.studentCardBack && (
                                <div className="p-6 bg-gray-50 rounded-xl">
                                  <label className="block mb-3 text-sm font-medium tracking-wide text-gray-500 uppercase">
                                    Back
                                  </label>
                                  <img
                                    src={formData.studentCardBack}
                                    alt="Student Card Back"
                                    className="object-cover w-full h-32 rounded-lg border"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                  onClick={handleSubmitStudentRegistration}
                  disabled={isSubmitting}
                  className={`px-10 py-4 text-lg font-semibold text-white rounded-xl shadow-lg transition-colors ${
                    isSubmitting
                      ? "bg-emerald-400 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {isSubmitting ? "Submitting..." : "Submit Registration"}
                </motion.button>
              </div>
            </motion.div>
          )}

          {currentStep === 4 && selectedType === "others" && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
                  Review & Submit
                </h1>
                <p className="mt-3 text-lg text-gray-600">
                  Please review your information before submitting
                </p>
              </div>

              {/* Preview Card */}
              <div className="mx-auto max-w-5xl">
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-xl">
                  {/* Header */}
                  <div className="px-8 py-6 bg-gradient-to-r from-emerald-500 to-emerald-600">
                    <h2 className="text-2xl font-semibold text-white">
                      Registration Summary
                    </h2>
                    <p className="text-lg text-emerald-100">
                      Please verify all information is correct
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-8 space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                        <div className="mr-4 w-3 h-3 bg-emerald-500 rounded-full"></div>
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            First Name
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.firstName}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Last Name
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.lastName}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Guardian Email
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.email}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Guardian Phone
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.phone}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Region
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.region}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Location
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.location}
                          </p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-xl">
                          <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                            Agent Code
                          </label>
                          <p className="text-lg font-medium text-gray-900">
                            {formData.agentCode}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ID Information (if provided) */}
                    {(formData.idType ||
                      formData.issueDate ||
                      formData.expiryDate ||
                      formData.idFrontImage ||
                      formData.idBackImage) && (
                      <div>
                        <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                          <div className="mr-4 w-3 h-3 bg-blue-500 rounded-full"></div>
                          ID Information
                        </h3>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          {formData.idType && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                ID Type
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {formData.idType}
                              </p>
                            </div>
                          )}
                          {formData.issueDate && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                Issue Date
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {new Date(
                                  formData.issueDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {formData.expiryDate && (
                            <div className="p-6 bg-gray-50 rounded-xl">
                              <label className="text-sm font-medium tracking-wide text-gray-500 uppercase">
                                Expiry Date
                              </label>
                              <p className="text-lg font-medium text-gray-900">
                                {new Date(
                                  formData.expiryDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* ID Images */}
                        {(formData.idFrontImage || formData.idBackImage) && (
                          <div className="mt-6">
                            <label className="block mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
                              ID Images
                            </label>
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                              {formData.idFrontImage && (
                                <div className="p-6 bg-gray-50 rounded-xl">
                                  <label className="block mb-3 text-sm font-medium tracking-wide text-gray-500 uppercase">
                                    Front
                                  </label>
                                  <img
                                    src={formData.idFrontImage}
                                    alt="ID Front"
                                    className="object-cover w-full h-32 rounded-lg border"
                                  />
                                </div>
                              )}
                              {formData.idBackImage && (
                                <div className="p-6 bg-gray-50 rounded-xl">
                                  <label className="block mb-3 text-sm font-medium tracking-wide text-gray-500 uppercase">
                                    Back
                                  </label>
                                  <img
                                    src={formData.idBackImage}
                                    alt="ID Back"
                                    className="object-cover w-full h-32 rounded-lg border"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                >
                  <ArrowLeft className="inline-block mr-3 w-5 h-5" />
                  Back
                </motion.button>

                <motion.button
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                  onClick={handleSubmitRegistration}
                  disabled={isSubmitting}
                  className={`px-10 py-4 text-lg font-semibold text-white rounded-xl shadow-lg transition-colors ${
                    isSubmitting
                      ? "bg-emerald-400 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {isSubmitting ? "Submitting..." : "Submit Registration"}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
}
